import { useState } from "react"
import Checkbox from "@/v2-ui/checkbox"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import LicenseFeatureGroupAccessModeSelect from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupAccessModeSelect"
import LicenseFeatureGroupContentGroup from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupContentGroup"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupPresetMenu from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupPresetMenu"
import { ROW_NOTE_CLASS } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  isCategoryRule,
  type FeatureGroupPickedLike,
  type FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  canGrantByCategory,
  describeImpact,
  grantOf,
  hasTypeRule,
  impactOf,
  matchesContentFilter,
  nounOf,
  packagesOf,
  rulesWithTypeRule,
  toPickedContent,
  type LicenseFeatureGroupContentClass as ContentClass,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"
import { useFeatureGroupLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import { reachesThroughLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"

export type LicenseFeatureGroupContentClassProps = {
  contentClass: ContentClass
  /** Category code → operator label, for the hover cards. */
  categoryLabelOf(code: string): string
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onRulesChange(rules: readonly FeatureGroupRuleLike[]): void
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
}

/** How many of the class each other package carries today, biggest first. */
function coverageOf(contentClass: ContentClass, featureGroupName: string) {
  const counts = new Map<string, number>()
  for (const item of contentClass.items) {
    for (const name of packagesOf(item)) {
      if (name === featureGroupName) continue
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1])
}

// One class of content — base maps, map layers — as a card. The header is
// the class's "everything" rule: ticked, the package carries every item
// there is and every one added later; anything less shows as indeterminate
// with the count. Beneath, the groups a user meets the items in (layer
// categories, countries), each opening to its items, and a filter for the
// hundreds of layers. The header's hover says in one sentence what the
// package does for a user here — because only LOCKED items depend on
// packages at all, and that is the first thing an operator needs to know.
function LicenseFeatureGroupContentClass(
  props: LicenseFeatureGroupContentClassProps,
) {
  const {
    contentClass,
    categoryLabelOf,
    rules,
    persistedRules,
    featureGroupName,
    isPicked,
    wasPicked,
    onRulesChange,
    onSetPicked,
  } = props

  const [filter, setFilter] = useState("")
  const lens = useFeatureGroupLens()
  const noun = nounOf(contentClass)
  const { featureTypeCode, items } = contentClass
  const isGranted = (item: ContentClass["items"][number]) =>
    grantOf(item, rules, isPicked) !== null
  const wasGranted = (item: ContentClass["items"][number]) =>
    grantOf(item, persistedRules, wasPicked) !== null
  // The counts and the sentence say what REACHES the people the lens is
  // about (through the customer lens, their packages count too); the
  // checkboxes and the +/− deltas stay the draft's own.
  const isReached = (item: ContentClass["items"][number]) =>
    reachesThroughLens(lens, item, isGranted(item))
  const impact = impactOf(items, isReached)
  const added = items.filter(
    (item) => isGranted(item) && !wasGranted(item),
  ).length
  const removed = items.filter(
    (item) => !isGranted(item) && wasGranted(item),
  ).length
  const isAllGranted = hasTypeRule(rules, featureTypeCode)
  const headerState =
    impact.granted === 0
      ? false
      : impact.granted === impact.total
        ? true
        : "indeterminate"
  const coverage = coverageOf(contentClass, featureGroupName)
  const isFiltering = filter.trim() !== ""
  const hasMatches = contentClass.groups.some((group) =>
    group.items.some((item) => matchesContentFilter(item, filter, group.label)),
  )

  function setSameAs(packageName: string) {
    const theirs = items.filter((item) =>
      packagesOf(item).includes(packageName),
    )
    // A sibling that carries the whole class is copied as the rule, not as
    // hundreds of picks; anything less is copied item by item.
    if (theirs.length === items.length && items.length > 0) {
      setClassGranted(true)
      return
    }
    setClassGranted(false)
    onSetPicked(theirs.map(toPickedContent), true)
  }

  function setClassGranted(granted: boolean) {
    // The class rule covers everything, so picks and category rules inside
    // the class are redundant either way: granting replaces them, clearing
    // takes everything with it.
    const withoutCategories = canGrantByCategory(featureTypeCode)
      ? rules.filter((rule) => !isCategoryRule(rule))
      : rules
    onRulesChange(
      rulesWithTypeRule(
        granted ? rules : withoutCategories,
        featureTypeCode,
        granted,
      ),
    )
    onSetPicked(items.map(toPickedContent), false)
  }

  return (
    <section className="min-w-0 overflow-hidden rounded-md border">
      <div className="flex items-center gap-2 border-b bg-muted/40 py-1.5 pl-2.5 pr-1.5">
        <Label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 font-normal">
          {lens.kind === "package" && (
            <Checkbox
              checked={headerState}
              aria-label={`Grant every ${noun}, now and in future releases`}
              onCheckedChange={(checked) => {
                setClassGranted(checked === true)
              }}
            />
          )}
          <LicenseFeatureGroupDetailsHoverCard
            title={contentClass.name}
            description={contentClass.description}
            facts={[
              { label: "For users", value: describeImpact(impact, noun, lens) },
              ...(lens.kind === "package"
                ? [
                    {
                      label: "Header tick",
                      value: isAllGranted
                        ? "the whole class as a rule: items added later are included"
                        : "grants the whole class as a rule, future items included; single ticks do not",
                    },
                    ...coverage.map(([name, count]) => ({
                      label: name,
                      value: `${count} of ${impact.total}`,
                    })),
                  ]
                : []),
            ]}
          >
            <span className="min-w-0 flex-1 truncate text-xs font-medium">
              {contentClass.name}
            </span>
          </LicenseFeatureGroupDetailsHoverCard>
          {isAllGranted && (
            <span className={ROW_NOTE_CLASS}>
              {lens.kind === "customer"
                ? `added here · all ${noun}s, incl. future additions`
                : "all, incl. future additions"}
            </span>
          )}
          {(added > 0 || removed > 0) && (
            <span className="text-[11px] tabular-nums text-primary">
              {added > 0 && `+${added}`}
              {added > 0 && removed > 0 && " "}
              {removed > 0 && `−${removed}`}
            </span>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">
            {impact.granted}/{impact.total}
            {lens.kind === "package" && impact.locked > 0 && (
              <span className="ml-1 text-[11px]">
                · {impact.locked} package-only
              </span>
            )}
          </span>
        </Label>
        <LicenseFeatureGroupAccessModeSelect
          featureTypeCode={featureTypeCode}
          subject={`${noun}s`}
        />
        {items.length > 12 && (
          <Input
            type="search"
            size="tiny"
            className="w-36"
            placeholder={`Find a ${noun}…`}
            aria-label={`Filter ${contentClass.name}`}
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value)
            }}
          />
        )}
        {items.length > 0 && lens.kind === "package" && (
          <LicenseFeatureGroupPresetMenu
            subject={contentClass.name.toLowerCase()}
            coverage={coverage}
            total={impact.total}
            onEverything={() => {
              setClassGranted(true)
            }}
            onNothing={() => {
              setClassGranted(false)
            }}
            onSameAs={setSameAs}
          />
        )}
      </div>
      <p className="border-b px-2.5 py-1 text-[11px] text-muted-foreground">
        {items.length === 0
          ? `No ${noun}s have a catalog row in this environment yet.`
          : describeImpact(impact, noun, lens)}
      </p>
      <ul className="divide-y">
        {contentClass.groups.map((group) => (
          <LicenseFeatureGroupContentGroup
            key={group.categoryCode ?? group.label}
            group={group}
            noun={noun}
            categoryLabelOf={categoryLabelOf}
            filter={filter}
            isAllGranted={isAllGranted}
            rules={rules}
            persistedRules={persistedRules}
            featureGroupName={featureGroupName}
            isPicked={isPicked}
            wasPicked={wasPicked}
            onRulesChange={onRulesChange}
            onSetPicked={onSetPicked}
          />
        ))}
      </ul>
      {isFiltering && !hasMatches && (
        <p className="px-2.5 py-2 text-xs text-muted-foreground">
          No {noun}s match “{filter.trim()}”.
        </p>
      )}
    </section>
  )
}

export default LicenseFeatureGroupContentClass
